//src/pages/inventory/Pharmacy/MedicineReturnModal.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  AlertTitle
} from "@mui/material";
import { returnMedicineToInventory } from "../../../services/medicineReturnService";
import { getMedicineInventoryList } from "../../../services/medicineInventoryService";
import { getMedicineTransactionsByPatientAndMedicine } from "../../../services/medicineTransactionService";
import { returnMedicineRequest } from "../../../services/medicineRequestService";
import { useAuth } from "../../../context/AuthContext";
import MedicationIcon from '@mui/icons-material/Medication';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../../utils/formModalStyles";

const MedicineReturnModal = ({ open, onClose, treatment, onSubmit, patientId, medicineRequest }) => {
  const { user } = useAuth();
  const [returnQuantities, setReturnQuantities] = useState({});
  const [returnReasons, setReturnReasons] = useState({});
  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [validationErrors, setValidationErrors] = useState({});
  const [inventoryDetails, setInventoryDetails] = useState(null);
  const [fetchingInventory, setFetchingInventory] = useState(false);
  const [issuedTransaction, setIssuedTransaction] = useState(null);

  useEffect(() => {
    if (treatment && open) {
      // Initialize return quantities and reasons
      const initialQuantities = {};
      const initialReasons = {};
      
      if (treatment.medicine) {
        const totalQuantity = calculateTotalDosage(treatment.dosage) * (treatment.duration || 1);
        initialQuantities[treatment.medicine] = totalQuantity; // Default to full quantity
        initialReasons[treatment.medicine] = "Unused medicine";
      }
      
      setReturnQuantities(initialQuantities);
      setReturnReasons(initialReasons);
      setValidationErrors({});
      
      // Fetch inventory details if medicineId is available
      if (treatment.medicineId) {
        fetchInventoryDetails(treatment.medicineId);
      }
    }
  }, [treatment, open]);

  const fetchInventoryDetails = async (medicineId) => {
    try {
      setFetchingInventory(true);
      
      // First, get the transaction history for this patient and medicine
      const transactions = await getMedicineTransactionsByPatientAndMedicine(patientId, medicineId);
      
      // Find the most recent OUT transaction (medicine issued to patient)
      const issuedTransaction = transactions
        .filter(tx => tx.transactionType === "OUT" && tx.patientType === "ipd")
        .sort((a, b) => new Date(b.transactionDate) - new Date(a.transactionDate))[0];
      
      if (!issuedTransaction) {
        showAlert("No medicine issuance record found for this patient and medicine", "error");
        return;
      }
      
      // Store the issued transaction for reference
      setIssuedTransaction(issuedTransaction);
      
      // Get the inventory details using the medicineInventoryId from the transaction
      const inventory = await getMedicineInventoryList();
      const medicineInventory = inventory.find(item => item.id === issuedTransaction.medicineInventoryId);
      
      if (medicineInventory) {
        setInventoryDetails(medicineInventory);
      } else {
        showAlert("Inventory record not found for the issued medicine", "error");
      }
    } catch (error) {
      console.error("Error fetching inventory details:", error);
      showAlert("Failed to fetch inventory details", "error");
    } finally {
      setFetchingInventory(false);
    }
  };

  const calculateTotalDosage = (dosage) => {
    if (!dosage) return 0;
    return dosage.split('-').reduce((sum, num) => sum + parseInt(num.trim() || 0), 0);
  };

  const showAlert = (message, type = "success") => {
    setAlertMessage(message);
    setAlertType(type);
    setAlertOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
    setAlertMessage("");
  };

  const validateReturn = () => {
    const errors = {};
    
    if (!treatment?.medicine) {
      errors.general = "No medicine found in treatment";
      return errors;
    }

    // Validate that we have the required inventory details
    if (!inventoryDetails) {
      errors.general = "Inventory details not found. Please wait for inventory data to load.";
      return errors;
    }

    if (!inventoryDetails.id) {
      errors.general = "Medicine inventory ID is required for return. Please ensure the medicine exists in inventory.";
      return errors;
    }

    const returnQty = returnQuantities[treatment.medicine] || 0;
    const totalQty = calculateTotalDosage(treatment.dosage) * (treatment.duration || 1);
    
    if (!returnQty || returnQty <= 0) {
      errors[treatment.medicine] = "Return quantity must be greater than 0";
    } else if (returnQty > totalQty) {
      errors[treatment.medicine] = `Return quantity cannot exceed total issued quantity (${totalQty})`;
    }

    if (!returnReasons[treatment.medicine]?.trim()) {
      errors[`${treatment.medicine}_reason`] = "Return reason is required";
    }

    return errors;
  };

  const handleReturn = async () => {
    try {
      setLoading(true);
      
      const errors = validateReturn();
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        showAlert("Please fix validation errors before proceeding.", "error");
        return;
      }
      
      setValidationErrors({});

      const returnData = {
        patientId: parseInt(patientId) || 0,
        userId: parseInt(user?.id) || 0,
        patientType: "ipd",
        transactionType: "IN",           // Adding to inventory
        transactionDate: new Date().toISOString(),
        medicineId: treatment.medicineId,
        medicineInventoryId: inventoryDetails.id, // Use fetched inventory ID
        medicineName: treatment.medicine,
        originalQuantity: calculateTotalDosage(treatment.dosage) * (treatment.duration || 1),
        returnQuantity: parseInt(returnQuantities[treatment.medicine]),
        returnReason: returnReasons[treatment.medicine],
        batchNumber: inventoryDetails.batchNumber || null,
        expiryDate: inventoryDetails.expiryDate || null,
        manufacturer: inventoryDetails.manufacturer || null,
        medicineType: inventoryDetails.medicineType || null,
        patientName: treatment.patientName || null, // Include patient name
      };
      

      // If this return is associated with a medicine request, use the request workflow
      if (medicineRequest && medicineRequest.id) {
        const requestReturnData = {
          returnedByUserId: parseInt(user?.id) || 0,
          returnedByName: user?.name || user?.username || "Unknown",
          returnedDate: new Date().toISOString(),
          returnReason: returnReasons[treatment.medicine],
          returnedQuantity: parseInt(returnQuantities[treatment.medicine]),
        };
        
        await returnMedicineRequest(medicineRequest.id, requestReturnData);
        showAlert("Medicine returned successfully via request workflow", "success");
      } else {
        // Fallback: Use the original return service (for medicines issued without request workflow)
        await returnMedicineToInventory(returnData);
        showAlert("Medicine returned to inventory successfully", "success");
      }
      
      setTimeout(() => {
        onSubmit(returnData);
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error("Error returning medicine:", error);
      
      let errorMessage = "Failed to return medicine";
      if (error.response) {
        errorMessage = `Server error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        errorMessage = "Network error: No response from server";
      } else {
        errorMessage = `Error: ${error.message}`;
      }
      
      showAlert(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!treatment) {
    return null;
  }

  const totalQuantity = calculateTotalDosage(treatment.dosage) * (treatment.duration || 1);
  const returnQty = returnQuantities[treatment.medicine] || 0;

  return (
    <>
      {/* Main Return Medicine Dialog */}
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
          Return IPD Medicine
        </DialogTitle>
        
        <DialogContent>
          {/* Treatment Information */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#fff3e0', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MedicationIcon color="primary" />
              Treatment Details
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip label={`Type: ${treatment.treatmentType}`} color="primary" variant="outlined" />
              <Chip label={`By: ${treatment.performedByName}`} color="secondary" variant="outlined" />
              {treatment.treatmentDate && (
                <Chip label={`Date: ${new Date(treatment.treatmentDate).toLocaleDateString()}`} color="default" variant="outlined" />
              )}
            </Box>
            <Divider sx={{ my: 1 }} />
          </Box>

          {/* Medicine Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: 'primary.main' }}>
              {treatment.medicine}
            </Typography>
            
            {fetchingInventory && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Loading inventory details for {treatment.medicine}...
                </Typography>
              </Alert>
            )}
            
            {issuedTransaction && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                 <strong>Found issued medicine record:</strong> Transaction ID {issuedTransaction.id} from {new Date(issuedTransaction.transactionDate).toLocaleDateString()}
                </Typography>
              </Alert>
            )}
            
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Details</strong></TableCell>
                    <TableCell><strong>Value</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Dosage</TableCell>
                    <TableCell>{treatment.dosage}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Duration</TableCell>
                    <TableCell>{treatment.duration} days</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Total Issued Quantity</TableCell>
                    <TableCell><strong>{totalQuantity} units</strong></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Inventory ID</TableCell>
                    <TableCell>
                      {fetchingInventory ? (
                        <Typography variant="body2" color="text.secondary">Loading...</Typography>
                      ) : (
                        <strong>{inventoryDetails?.id || "Not Available"}</strong>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Batch Number</TableCell>
                    <TableCell>
                      {fetchingInventory ? (
                        <Typography variant="body2" color="text.secondary">Loading...</Typography>
                      ) : (
                        inventoryDetails?.batchNumber || "N/A"
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Manufacturer</TableCell>
                    <TableCell>
                      {fetchingInventory ? (
                        <Typography variant="body2" color="text.secondary">Loading...</Typography>
                      ) : (
                        inventoryDetails?.manufacturer || "N/A"
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Return Form */}
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                Return Details
              </Typography>
              
              <Box display="flex" gap={2} sx={{ justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
                <TextField
                  label="Return Quantity"
                  type="number"
                  value={returnQuantities[treatment.medicine] || ""}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (isNaN(value)) {
                      setReturnQuantities({ ...returnQuantities, [treatment.medicine]: "" });
                      return;
                    }
                    setReturnQuantities({
                      ...returnQuantities,
                      [treatment.medicine]: value
                    });
                  }}
                  error={!!validationErrors[treatment.medicine]}
                  helperText={validationErrors[treatment.medicine] || `Maximum: ${totalQuantity} units`}
                  fullWidth
                  margin="normal"
                  inputProps={{ min: 1, max: totalQuantity }}
                  sx={{ maxWidth: '200px' }}
                  required
                />
                
                <TextField
                  label="Return Reason"
                  value={returnReasons[treatment.medicine] || ""}
                  onChange={(e) => {
                    setReturnReasons({
                      ...returnReasons,
                      [treatment.medicine]: e.target.value
                    });
                  }}
                  error={!!validationErrors[`${treatment.medicine}_reason`]}
                  helperText={validationErrors[`${treatment.medicine}_reason`]}
                  fullWidth
                  margin="normal"
                  sx={{ maxWidth: '300px' }}
                  required
                />
              </Box>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> Returning {returnQty} units out of {totalQuantity} total issued units.
                  {returnQty < totalQuantity && ` Remaining ${totalQuantity - returnQty} units will be considered as used.`}
                </Typography>
              </Alert>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button onClick={onClose} color="primary" variant="outlined" sx={FORM_CANCEL_BUTTON_SX} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleReturn} 
            color="primary" 
            variant="contained"
            sx={FORM_SUBMIT_BUTTON_SX}
            disabled={!treatment.medicine || loading || fetchingInventory || !inventoryDetails}
          >
            {loading ? "Processing..." : fetchingInventory ? "Loading Inventory..." : "Return Medicine"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Popup */}
      <Dialog 
        open={alertOpen} 
        onClose={handleCloseAlert}
        maxWidth="md" 
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <Alert 
          severity={alertType} 
          onClose={handleCloseAlert}
          sx={{ 
            fontSize: '1.1rem',
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <AlertTitle sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
            {alertType === "success" ? "Success!" : "Error!"}
          </AlertTitle>
          {alertMessage}
        </Alert>
      </Dialog>
    </>
  );
};

export default MedicineReturnModal;
